import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import dayjs, { Dayjs } from 'dayjs'
import { dashboardPageContainerStyle } from '@/constants/layout'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { getSurveyNpsSummary, getSurveyStatistics } from '@/apis/aipass'
import { NpsSummaryResponse } from '@/models/survey/nps-summary'
import { SurveyStatisticsResponse } from '@/models/survey/statistics'
import { SurveyTab } from '@/components/pages/survey/tab'
import { DashboardPageLayout } from '@/components/layouts/dashboard-page-layout'
import { CompanionType, AgeGroupType } from '@/models/survey/filter-types'
import { SurveySummaryHeader } from '@/components/organisms/survey/summary/header'
import { SurveySummaryMainSection } from '@/components/organisms/survey/summary/main-section'
import { SurveySummaryQuestions } from '@/components/organisms/survey/summary/questions-section'

// フィルターの全オプション（初回ロード時に全てチェック済みにするため、未選択も含む）
const ALL_COMPANION_TYPES: CompanionType[] = ['none', 'alone', 'family', 'friend', 'partner', 'coworker', 'other']
const ALL_AGE_GROUP_TYPES: AgeGroupType[] = ['none', '10', '20', '30', '40', '50', '60', '70']

/**
 * NPSサマリーと統計データを取得し、各セクションに表示する
 */
export const SurveySummary: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [loading, setLoading] = useState<boolean>(true)
  const [month, setMonth] = useState<Dayjs>(dayjs())
  const [companion, setCompanion] = useState<CompanionType[]>(ALL_COMPANION_TYPES)
  const [ageGroup, setAgeGroup] = useState<AgeGroupType[]>(ALL_AGE_GROUP_TYPES)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const [npsSummary, setNpsSummary] = useState<NpsSummaryResponse | null>(null)
  const [statistics, setStatistics] = useState<SurveyStatisticsResponse | null>(null)

  // NPSサマリーと統計データを並列で取得（それぞれ独立してエラーハンドリング）
  const loadData = useCallback(async () => {
    setLoading(true)
    const monthStr = month.format('YYYYMM')

    // すべて解除されている場合は0件表示（APIを呼ばない）
    if (companion.length === 0 && ageGroup.length === 0) {
      setNpsSummary(null)
      setStatistics(null)
      setLoading(false)
      return
    }

    // NPSサマリーデータの取得
    const loadNpsData = async () => {
      try {
        const npsData = await getSurveyNpsSummary({
          month: monthStr,
          periodMonths: 13, // 過去13ヶ月分のデータを取得
          companion: companion.length > 0 ? companion : undefined,
          ageGroup: ageGroup.length > 0 ? ageGroup : undefined,
        })
        setNpsSummary(npsData)
      } catch (error: any) {
        // データがない場合（404など）はアラートを出さない
        const status = error?.response?.status
        if (status !== 404) {
          errorHandler(error)
        }
        setNpsSummary(null)
      }
    }

    // 統計データの取得
    const loadStatsData = async () => {
      try {
        const statsData = await getSurveyStatistics({
          month: monthStr,
          companion: companion.length > 0 ? companion : undefined,
          ageGroup: ageGroup.length > 0 ? ageGroup : undefined,
        })
        setStatistics(statsData)
      } catch (error: any) {
        // データがない場合（404など）はアラートを出さない
        const status = error?.response?.status
        if (status !== 404) {
          errorHandler(error)
        }
        setStatistics(null)
      }
    }

    // 並列で実行し、それぞれ独立してエラーハンドリング
    await Promise.allSettled([loadNpsData(), loadStatsData()])
    setLoading(false)
  }, [month, companion, ageGroup, errorHandler])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleFilterChange = (newCompanion: CompanionType[], newAgeGroup: AgeGroupType[]) => {
    setCompanion(newCompanion)
    setAgeGroup(newAgeGroup)
  }

  return (
    <DashboardPageLayout title={t('Survey')} loading={loading}>
      <>
        {/* タブ */}
        <div css={headerContainerStyle}>
          <SurveyTab currentTab="summary" />
        </div>
        <div css={dashboardPageContainerStyle}>
          <SurveySummaryHeader
            month={month}
            onMonthChange={setMonth}
            companion={companion}
            ageGroup={ageGroup}
            onFilterChange={handleFilterChange}
            isFilterOpen={isFilterOpen}
            onFilterOpen={() => setIsFilterOpen(true)}
            onFilterClose={() => setIsFilterOpen(false)}
          />
          <SurveySummaryMainSection npsSummary={npsSummary} month={month} />
          <SurveySummaryQuestions statistics={statistics} />
        </div>
      </>
    </DashboardPageLayout>
  )
}

const headerContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})
